import { useEffect, useState } from "react";
import { api, INR, compactINR } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

const PERIODS = [
  { id: "this_month", label: "This Month" },
  { id: "last_month", label: "Last Month" },
  { id: "last_3_months", label: "Last 3 Months" },
  { id: "custom", label: "Custom" },
];

export default function Reports() {
  const [affs, setAffs] = useState([]);
  const [asms, setAsms] = useState([]);
  const [active, setActive] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [period, setPeriod] = useState("this_month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  useEffect(() => {
    api.get("/affiliates").then((r) => setAffs(r.data));
    api.get("/users").then((r) => setAsms(r.data.filter((u) => u.role === "asm")));
    api.get("/reports/active-affiliates").then((r) => setActive(r.data));
  }, []);
  useEffect(() => {
    const params = { period };
    if (period === "custom") {
      if (!customStart || !customEnd) return;
      params.start = customStart;
      params.end = customEnd;
    }
    setMetrics(null);
    api
      .get("/reports/program-metrics", { params })
      .then((r) => setMetrics(r.data))
      .catch(() => setMetrics(null));
  }, [period, customStart, customEnd]);

  const asmData = asms.map((asm) => {
    const cohort = affs.filter((a) => a.assigned_asm_id === asm.id);
    const sales = cohort.reduce((s, a) => s + (a.current_month_sales || 0), 0);
    const newC = cohort.reduce((s, a) => s + (a.current_month_new_customers || 0), 0);
    return { name: asm.name, sales, newC, count: cohort.length };
  });

  const tierAggregate = ["Base", "Pro", "Pro Advanced", "Legend"].map((t) => ({
    tier: t,
    count: affs.filter((a) => a.tier === t).length,
  }));

  return (
    <div>
      <PageHeader title="Reports" subtitle="Team performance & cohort analysis" />
      <div className="p-6 md:p-10 space-y-6">
        {/* Program Averages — with period picker */}
        <div data-testid="program-metrics-card" className="rounded-2xl border border-border/60 bg-card p-6">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <h3 className="text-lg font-semibold tracking-tight">Program Averages</h3>
            <span className="text-xs text-muted-foreground">{metrics?.label || ""}</span>
            <div className="ml-auto flex flex-wrap gap-2 items-center">
              {PERIODS.map((p) => (
                <button
                  key={p.id}
                  data-testid={`period-${p.id}`}
                  onClick={() => setPeriod(p.id)}
                  className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
                    period === p.id
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border/60 hover:bg-secondary"
                  }`}
                >
                  {p.label}
                </button>
              ))}
              {period === "custom" && (
                <>
                  <input
                    type="date"
                    data-testid="metrics-custom-start"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="h-8 rounded-full border border-border bg-card px-3 text-xs"
                  />
                  <span className="text-xs text-muted-foreground">to</span>
                  <input
                    type="date"
                    data-testid="metrics-custom-end"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="h-8 rounded-full border border-border bg-card px-3 text-xs"
                  />
                </>
              )}
            </div>
          </div>
          {metrics ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div data-testid="metric-avg-revenue" className="rounded-xl p-5 bg-primary/10">
                <div className="text-[10px] uppercase tracking-widest text-primary">Avg Revenue / Affiliate</div>
                <div className="text-3xl font-bold mt-2 text-primary">{compactINR(metrics.avg_revenue_per_affiliate)}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {INR(metrics.total_revenue)} across {metrics.total_affiliates} affiliates
                </div>
              </div>
              <div data-testid="metric-avg-new" className="rounded-xl p-5 bg-secondary/40">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Avg New Customers / Affiliate</div>
                <div className="text-3xl font-bold mt-2">{metrics.avg_new_customers_per_affiliate}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {metrics.total_new_customers} new customers total
                </div>
              </div>
              <div data-testid="metric-cohort" className="rounded-xl p-5 bg-secondary/40">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Cohort %</div>
                <div className="text-3xl font-bold mt-2">{metrics.cohort_percentage}%</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {metrics.cohort_count} of {metrics.total_affiliates} sold in period
                </div>
              </div>
              <div className="rounded-xl p-5 bg-secondary/40">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Approved-Only Averages</div>
                <div className="text-lg font-semibold mt-2">{compactINR(metrics.avg_revenue_per_approved)} · {metrics.avg_new_customers_per_approved} new</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Across {metrics.approved_affiliates} approved
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground py-6 text-center">
              {period === "custom" && (!customStart || !customEnd) ? "Pick a start and end date" : "Loading…"}
            </div>
          )}
        </div>

        {/* Active Affiliate % */}
        {active && (
          <div data-testid="active-report-card" className="rounded-2xl border border-border/60 bg-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <h3 className="text-lg font-semibold tracking-tight">Active Affiliates</h3>
              <span className="text-xs text-muted-foreground">Approved affiliates who have generated at least 1 sale</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div data-testid="active-percent" className="rounded-xl p-5 bg-primary/10">
                <div className="text-[10px] uppercase tracking-widest text-primary">Active %</div>
                <div className="text-4xl font-bold mt-2 text-primary">{active.active_percentage}%</div>
                <div className="text-xs text-muted-foreground mt-1">{active.active} of {active.approved} approved</div>
              </div>
              <div className="rounded-xl p-5 bg-secondary/40">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total</div>
                <div className="text-3xl font-semibold mt-2">{active.total}</div>
              </div>
              <div className="rounded-xl p-5 bg-secondary/40">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Approved</div>
                <div className="text-3xl font-semibold mt-2">{active.approved}</div>
              </div>
              <div className="rounded-xl p-5 bg-secondary/40">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Paused</div>
                <div className="text-3xl font-semibold mt-2">{active.paused}</div>
              </div>
              <div className="rounded-xl p-5 bg-secondary/40">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Approved · No Sales</div>
                <div className="text-3xl font-semibold mt-2">{active.inactive_approved}</div>
              </div>
            </div>
          </div>
        )}
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <h3 className="text-lg font-semibold tracking-tight mb-4">ASM Cohort — This Month</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={asmData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`} />
                <Tooltip
                  formatter={(v) => INR(v)}
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }}
                />
                <Bar dataKey="sales" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            {asmData.map((d) => (
              <div key={d.name} className="rounded-xl bg-secondary/40 p-4 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold">{d.name}</div>
                  <div className="text-xs text-muted-foreground">{d.count} affiliates · {d.newC} new this month</div>
                </div>
                <div className="text-xl font-semibold">{INR(d.sales)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <h3 className="text-lg font-semibold tracking-tight mb-4">Tier Distribution</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={tierAggregate}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="tier" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                <Bar dataKey="count" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
